<template>
  <v-card>
    <v-card-title>
      Role Permissions
    </v-card-title>
    <v-card-actions>
      <DebouncedTextField
        v-model="search"
        label="search"
      />
      <v-spacer/>
      <v-btn @click="createRole()">Add</v-btn>
      <v-btn @click="showEmpty=!showEmpty">Show Empty</v-btn>
    </v-card-actions>
    <v-card-text>
        <span
          class="caption"> Notes:
          <ul>
            <li>if no permissions are specified, the role will have read/write access on all pages.</li>
            <li>if a user has 'writePage' permissions, they can also read (you don't need to add the page twice)</li>
          </ul>
        </span>
      <v-data-table
        :items="filteredRoles"
        :headers="headers"
        :custom-filter="valueSearch"
        disable-pagination
        hide-default-footer
        sort-by="name"
        :search="search"
      >
        <template v-slot:item.email="{item}">
          <a :href="`mailto:${item.email}`">{{ item.email }}</a>
        </template>
        <template v-slot:item.readPages="{item}">
          <div class="d-flex align-center">
            <div class="flex-grow-1">
              <v-chip
                v-for="page in pageChips(item.readPages)"
                :key="page.name"
                class="ma-1"
                small
              >
                {{ page.path }}
              </v-chip>
              <span v-if="pageChips(item.readPages).length === 0" class="caption grey--text">no restrictions</span>
            </div>
            <v-btn
              icon
              small
              :disabled="!item.edit"
              title="edit pages"
              @click="editPages(item, 'readPages')"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:item.writePages="{item}">
          <div class="d-flex align-center">
            <div class="flex-grow-1">
              <v-chip
                v-for="page in pageChips(item.writePages)"
                :key="page.name"
                class="ma-1"
                small
              >
                {{ page.path }}
              </v-chip>
              <span v-if="pageChips(item.writePages).length === 0" class="caption grey--text">no restrictions</span>
            </div>
            <v-btn
              icon
              small
              :disabled="!item.edit"
              title="edit pages"
              @click="editPages(item, 'writePages')"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:item.actions="{item}">
          <v-btn icon @click="$set(item,'edit',true)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon @click="deleteRole(item)">
            <v-icon>mdi-trash-can</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>
    <RolePagesDialog
      v-model="pagesDialog.show"
      :title="pagesDialog.title"
      :pages="pagesDialog.pages"
      :routes="routes"
      @save="savePages"
    />
  </v-card>
</template>

<script>
import {mapGetters} from 'vuex';
import api from '@/api';
import DebouncedTextField from "@/components/DebouncedTextField.vue";
import RolePagesDialog from "@/components/RolePagesDialog.vue";
import {valueSearch} from "../store/utils";


export default {
  name: "Roles",
  components: {DebouncedTextField, RolePagesDialog},
  data() {
    return {
      roles: [],
      diets: [],
      headers: 'id,name,readPages,writePages,api,users,actions'
        .split(',')
        .map(p => ({value: p, text: p})),
      showDeleted: false,
      routes: [],
      showEmpty: null,
      search: null,
      pagesDialog: {
        show: false,
        title: '',
        role: null,
        field: null,
        pages: []
      }
    }
  },
  mounted() {
    this.$router.options.routes.forEach(route => {
      if (route.meta && route.meta.requiresAuth) {
        this.routes.push({
          name: route.name
          , path: route.path
        });
      }
    });
    this.routes.sort((a, b) => a.path.localeCompare(b.path));
    this.fetchRoles();
  },
  computed: {
    ...mapGetters([
      'getCurrentUser'
    ]),
    filteredRoles() {
      const routes = this.routes;
      const roles = this.roles
        .map(r => {
          return ({
            ...r,
            routes: JSON.stringify(r.readPages.map(r => {
              const route = routes.find(route => route.name === r);
              return route && route.path
            }))
          });
        });
      if (this.showEmpty) {
        return roles;
      } else {
        return roles.filter(r => r.users.length > 0);
      }
    }
  },
  methods: {
    valueSearch,
    pageChips(pageNames) {
      return (pageNames || []).map(name => {
        const route = this.routes.find(r => r.name === name);
        return route || {name, path: name};
      });
    },
    editPages(role, field) {
      const label = field === 'readPages' ? 'Read pages' : 'Write pages';
      this.pagesDialog = {
        show: true,
        title: `${label}: ${role.name}`,
        role,
        field,
        pages: role[field] || []
      };
    },
    savePages(pages) {
      const {role, field} = this.pagesDialog;
      if (!role) {
        return;
      }
      role[field] = pages;
      this.saveRole(role);
    },
    saveRole(role) {
      console.log('saving', JSON.stringify(role));
      api.put(`v2/role/${role.id}`, role)
        .finally(this.fetchRoles);
    },
    createRole() {
      console.log('creating role',);
      const name = prompt('enter name of role')
      api.post(`v2/role/`, {name})
        .finally(this.fetchRoles);
    },
    deleteRole(role) {
      return api
        .get('users/list')
        .then(response => {
          const users = response.data;
          return users;
        })
        .then(users => {
          const usersWithRole = users.filter(user =>
            user.role === role.id || (user.roles && user.roles.includes(role.id))
          );
          if (usersWithRole.length > 0) {
            alert(`you cannot delete this role as it is still being used by these users: ${usersWithRole.map(u => u.email).join(',')}`);
            throw new Error('role in use');
          }
        })
        .then(() => {
          api.delete(`v2/role/${role.id}`)
            .then(this.fetchRoles);
        })
        .catch(e => {
          console.warn('delete role failed', e);
        });
    },
    fetchRoles() {
      return api.get('v2/role')
        .then(response => {
          this.roles = response.data
            .map(r => ({...r, users: r.users.map(u => u.email).join(', ')}))
            .sort((a, b) => a.name.localeCompare(b.name));
        })
    }
  }
}
</script>

<style scoped>

</style>
